// src/features/simulators/shared/utils/journeyDataGenerator.ts

import { 
  GuestType, 
  JourneyState, 
  SectionCode,
  QRCodeType,
} from '../types/journey.types';

/**
 * Enhanced data generator for journey simulations
 * Generates realistic data following correlation requirements
 */

/**
 * Generate a realistic reservation number
 * Can be OTA format (BKNG123456) or PMS format (RES-2024-001)
 */
export function generateReservationNumber(type: 'OTA' | 'PMS' | 'RANDOM' = 'RANDOM'): string {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);

  if (type === 'OTA' || (type === 'RANDOM' && Math.random() > 0.5)) {
    // OTA format: BKNG123456, EXPD987654, etc.
    const otaPrefixes = ['BKNG', 'EXPD', 'HTLS', 'AGOD', 'VRBO'];
    const prefix = otaPrefixes[Math.floor(Math.random() * otaPrefixes.length)];
    return `${prefix}${random.toString().padStart(6, '0')}`;
  } else {
    // PMS format: RES-2024-001
    const year = new Date().getFullYear();
    const sequence = (timestamp % 1000).toString().padStart(3, '0');
    return `RES-${year}-${sequence}${random.toString().padStart(3, '0')}`;
  }
}

/**
 * Generate alternate reservation numbers
 * Simulates the case where OTA and PMS use different numbers
 */
export function generateAlternateReservationNumbers(
  primaryReservation: string
): string[] {
  const alternates: string[] = [];
  
  // If primary is OTA, add a PMS number
  if (primaryReservation.match(/^[A-Z]{4}\d+$/)) {
    alternates.push(generateReservationNumber('PMS'));
  }
  // If primary is PMS, add an OTA number
  else if (primaryReservation.startsWith('RES-')) {
    alternates.push(generateReservationNumber('OTA'));
  }

  // Sometimes add additional alternate (e.g., group booking reference)
  if (Math.random() > 0.7) {
    alternates.push(`GRP-${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`);
  }

  return alternates;
}

/**
 * Generate a realistic room number
 */
export function generateRoomNumber(): string {
  const floor = Math.floor(Math.random() * 5) + 1; // Floors 1-5
  const room = Math.floor(Math.random() * 20) + 1; // Rooms 01-20 per floor
  return `${floor}${room.toString().padStart(2, '0')}`;
}

/**
 * Generate a realistic full guest name, as the property system returns it to the kiosk
 * (given name(s) + two surnames). Insight reduces it to «Juan G.» on ingestion.
 */
export function generateGuestName(): { full: string; masked: string } {
  const firstNames = ['Juan Carlos', 'María', 'Carlos', 'Emma', 'Luis', 'Ana Belén', 'Peter', 'Sofía', 'Ángel', 'Lucía'];
  const lastNames = ['García', 'Smith', 'Rodríguez', 'Johnson', 'Martínez', 'Brown', 'López', 'Wilson', 'Óscar', 'Ortega'];
  const pick = (list: string[]) => list[Math.floor(Math.random() * list.length)];

  const first = pick(firstNames);
  const last1 = pick(lastNames);
  const last2 = pick(lastNames);

  return {
    full: `${first} ${last1} ${last2}`,
    masked: `${first.split(' ')[0]} ${last1.charAt(0)}.`
  };
}

/**
 * Generate a masked document number
 */
export function generateDocument(): { full: string; masked: string; type: string } {
  const types = ['passport', 'dni', 'driver_license'];
  const type = types[Math.floor(Math.random() * types.length)];
  
  let full: string;
  let masked: string;
  
  switch (type) {
    case 'passport':
      const passportNum = Math.random().toString(36).substring(2, 11).toUpperCase();
      full = passportNum;
      masked = `${passportNum.substring(0, 2)}****${passportNum.substring(7)}`;
      break;
    case 'dni':
      const dniNum = Math.floor(Math.random() * 100000000).toString().padStart(8, '0');
      const dniLetter = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(Math.floor(Math.random() * 26));
      full = `${dniNum}${dniLetter}`;
      masked = `****${dniNum.substring(4)}${dniLetter}`;
      break;
    default:
      const dlNum = Math.random().toString(36).substring(2, 10).toUpperCase();
      full = dlNum;
      masked = `${dlNum.substring(0, 2)}****${dlNum.substring(6)}`;
  }
  
  return { full, masked, type };
}

/**
 * Generate payment data
 */
export function generatePaymentData(): {
  amount: number;
  method: string;
  operationId: string;
  currency: string;
} {
  const methods = ['credit_card', 'debit_card', 'cash', 'bank_transfer'];
  const baseAmounts = [100, 150, 200, 250, 300, 500, 750, 1000];
  
  return {
    amount: baseAmounts[Math.floor(Math.random() * baseAmounts.length)],
    method: methods[Math.floor(Math.random() * methods.length)],
    operationId: `PAY-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
    currency: 'EUR'
  };
}

/**
 * Generate section duration (realistic times in seconds)
 */
export function generateSectionDuration(section: SectionCode): number {
  const durations: Record<SectionCode, { min: number; max: number }> = {
    [SectionCode.S]: { min: 15, max: 60 },   // Search: 15-60s
    [SectionCode.R]: { min: 30, max: 120 },  // Room selection: 30-120s
    [SectionCode.I]: { min: 45, max: 180 },  // Identification: 45-180s
    [SectionCode.F]: { min: 20, max: 60 },   // Signature: 20-60s
    [SectionCode.U]: { min: 10, max: 90 },   // Upselling: 10-90s
    [SectionCode.P]: { min: 30, max: 120 },  // Payment: 30-120s
    [SectionCode.K]: { min: 5, max: 30 },    // Key delivery: 5-30s
  };
  
  const range = durations[section] || { min: 10, max: 60 };
  return Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
}

/**
 * Generate a unique QR identifier for correlation
 */
export function generateQRUniqueId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `QR-${timestamp}-${random}`.toUpperCase();
}

/**
 * Generate QR code data based on type
 */
export function generateQRData(
  type: QRCodeType,
  journey: JourneyState
): { 
  qrData: string; 
  qrUniqueId?: string;
} {
  switch (type) {
    case QRCodeType.RESERVATION_NUMBER:
      // QR contains only reservation number
      return {
        qrData: journey.reservationNumber || '',
        qrUniqueId: undefined
      };
      
    case QRCodeType.UNIQUE_ID:
      // QR contains unique identifier for correlation
      const uniqueId = generateQRUniqueId();
      return {
        qrData: uniqueId,
        qrUniqueId: uniqueId
      };
      
    case QRCodeType.FULL_INFO:
      // QR contains room + metadata (but needs unique ID for correlation)
      const fullInfoId = generateQRUniqueId();
      return {
        qrData: JSON.stringify({
          room: journey.roomNumber,
          id: fullInfoId,
          reservation: journey.reservationNumber
        }),
        qrUniqueId: fullInfoId
      };
      
    default:
      return { qrData: '', qrUniqueId: undefined };
  }
}

/**
 * Generate error codes for different scenarios
 */
export function generateErrorCode(context: 'payment' | 'key' | 'system' = 'system'): string {
  const errorCodes = {
    payment: ['CARD_DECLINED', 'INSUFFICIENT_FUNDS', 'CARD_EXPIRED', 'INVALID_CVV', 'BANK_TIMEOUT'],
    key: ['MECH_JAM', 'NO_KEY_AVAILABLE', 'DISPENSER_OFFLINE', 'INVALID_ROOM', 'CARD_ENCODE_ERROR'],
    system: ['PMS_TIMEOUT', 'NETWORK_ERROR', 'DATABASE_ERROR', 'SERVICE_UNAVAILABLE', 'INVALID_DATA']
  };
  
  const codes = errorCodes[context];
  return codes[Math.floor(Math.random() * codes.length)];
}

/**
 * Generate device ID based on type
 */
export function generateDeviceId(type: 'kiosk' | 'dispenser' | 'mobile' | 'web'): string {
  const timestamp = Date.now().toString().slice(-4);
  
  switch (type) {
    case 'kiosk':
      return `KR${Math.floor(Math.random() * 100).toString().padStart(3, '0')}.${Math.floor(Math.random() * 10)}`;
    case 'dispenser':
      return `DISP-${Math.floor(Math.random() * 100).toString().padStart(3, '0')}`;
    case 'mobile':
      return `MOB-${timestamp}`;
    case 'web':
      return `WEB-CHECKIN-${Math.floor(Math.random() * 10).toString().padStart(2, '0')}`;
    default:
      return `DEVICE-${timestamp}`;
  }
}

/**
 * Create a new journey state
 */
export function createJourneyState(config: {
  guestType: GuestType;
  hasPreCheckIn?: boolean;
  roomNumber?: string;
  reservationNumber?: string;  // NEW: Optional custom reservation number
}): JourneyState {
  const isWalking = config.guestType === GuestType.WALKING;
  
  const journey: JourneyState = {
    journeyId: `JOURNEY-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
    // Use custom reservation number if provided, otherwise generate
    reservationNumber: isWalking 
      ? null 
      : (config.reservationNumber || generateReservationNumber()),
    alternateReservationNumbers: [],
    roomNumber: config.roomNumber,
    isWalking,
    hasPreCheckIn: config.hasPreCheckIn || false,
    completedSections: [],
    preCheckInStarted: false,
    preCheckInCompleted: false,
    checkInStarted: false,
    checkInCompleted: false,
    keyDispensed: false
  };
  
  return journey;
}

/**
 * Generate abandonment reason
 */
export function generateAbandonmentReason(): string {
  const reasons = [
    'timeout',           // User inactive for too long
    'user_exit',        // User clicked exit/cancel
    'technical_issue',  // System error
    'changed_mind',     // User decided not to continue
    'assistance_needed' // User went to reception
  ];
  
  return reasons[Math.floor(Math.random() * reasons.length)];
}

/**
 * Generate heartbeat metadata
 */
export function generateHeartbeatMetadata(startTime: Date): {
  status: string;
  uptime: string;
  version: string;
  lastActivity?: string;
} {
  const statuses = ['idle', 'busy', 'maintenance', 'active'];
  const uptime = Math.floor((Date.now() - startTime.getTime()) / 1000);
  
  return {
    status: statuses[Math.floor(Math.random() * statuses.length)],
    uptime: uptime.toString(),
    version: '2.4.1',
    lastActivity: new Date().toISOString()
  };
}

/**
 * Check if should skip a section based on configuration
 */
export function shouldSkipSection(
  section: SectionCode,
  skipSections: SectionCode[] = [],
  skipProbability: number = 0.1
): boolean {
  // Always skip if in skip list
  if (skipSections.includes(section)) {
    return true;
  }
  
  // Optional sections can be randomly skipped
  const optionalSections = [SectionCode.U]; // Upselling is optional
  if (optionalSections.includes(section)) {
    return Math.random() < skipProbability;
  }
  
  return false;
}

/**
 * Determine if an event should fail based on error rates
 */
export function shouldFail(errorRate: number): boolean {
  return Math.random() < errorRate;
}

/**
 * Generate realistic check-in and check-out dates
 */
export function generateStayDates(): {
  checkIn: string;
  checkOut: string;
  nights: number;
} {
  const today = new Date();
  const checkInDate = new Date(today);
  
  // Check-in can be today or in the next 30 days
  checkInDate.setDate(checkInDate.getDate() + Math.floor(Math.random() * 30));
  
  // Stay duration: 1-7 nights (weighted towards 2-3 nights)
  const weights = [1, 3, 3, 2, 1, 1, 1]; // Weights for 1-7 nights
  const nights = weightedRandom(weights) + 1;
  
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkOutDate.getDate() + nights);
  
  return {
    checkIn: checkInDate.toISOString().split('T')[0],
    checkOut: checkOutDate.toISOString().split('T')[0],
    nights
  };
}

/**
 * Helper function for weighted random selection
 */
function weightedRandom(weights: number[]): number {
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  const random = Math.random() * totalWeight;
  
  let accumulator = 0;
  for (let i = 0; i < weights.length; i++) {
    accumulator += weights[i];
    if (random < accumulator) {
      return i;
    }
  }
  
  return weights.length - 1;
}

/**
 * Generate hotel and group IDs
 */
export function generateHotelConfig(): {
  hotelId: string;
  groupId?: string;
  hotelName: string;
} {
  const hotelNames = [
    'Hotel Plaza Mayor',
    'Grand Hotel Europa',
    'Hotel Costa del Sol',
    'Hotel Montaña Verde',
    'Hotel Ciudad Antigua'
  ];
  
  const hotelName = hotelNames[Math.floor(Math.random() * hotelNames.length)];
  const hotelId = `HTL-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  
  // 70% chance of being part of a group
  const hasGroup = Math.random() < 0.7;
  const groupId = hasGroup ? `GRP-${Math.random().toString(36).substring(2, 6).toUpperCase()}` : undefined;
  
  return { hotelId, groupId, hotelName };
}